import type { FilterRule, ListBuilderConfiguration, ListBuilderList } from '../../types';
import { createClientJobColumns, createDefaultJobColumns, JOB_TABLE } from './columns';

/**
 * PD-17093 — the Jobs page (Sales → Jobs) and a customer's Jobs tab. Rows come
 * from GET /api/jobs (JobsService), which applies every filter here itself.
 * Each tab's defining predicate is a LOCKED, hidden rule, so the server applies
 * it through `getEnforcedFilters` (the list and its tab count agree).
 */
const OPEN_STATUS_RULE: FilterRule = {
	defaultValue: [
		{ id: 'planned', label: 'Planned' },
		{ id: 'active', label: 'Active' },
		{ id: 'on_hold', label: 'On Hold' },
	],
	locked: true,
	hidden: true,
};

const statusRule = (id: string, label: string): FilterRule => ({
	defaultValue: [{ id, label }],
	locked: true,
	hidden: true,
});

const list = (
	id: string,
	name: string,
	sortOrder: number,
	description: string,
	filterRules: Record<string, FilterRule>,
	columns = createDefaultJobColumns(),
	defaultSortId = 'start-desc',
): ListBuilderList => ({
	id,
	cacheTTL: 600000,
	name,
	type: 'default',
	active: true,
	sortOrder,
	columns,
	description,
	filterRules,
	defaultSortId,
});

const JOB_OBJECT = {
	baseUrl: '/jobs/:id',
	objectLabel: 'Job',
	objectTable: JOB_TABLE,
	rowIdField: 'id',
	selectedFields: [
		{ fieldName: '_id', fieldLabel: 'ID' },
		{ fieldName: 'name', fieldLabel: 'Name' },
		{ fieldName: 'jobNumber', fieldLabel: 'Job Number' },
		{ fieldName: 'status', fieldLabel: 'Status' },
		{ fieldName: 'clientName', fieldLabel: 'Customer' },
		{ fieldName: 'salespersonName', fieldLabel: 'Salesperson' },
		{ fieldName: 'startDate', fieldLabel: 'Start Date' },
		{ fieldName: 'endDate', fieldLabel: 'End Date' },
		{ fieldName: 'estimatedValue', fieldLabel: 'Estimated Value' },
		{ fieldName: 'quotedTotal', fieldLabel: 'Quoted' },
		{ fieldName: 'orderedTotal', fieldLabel: 'Ordered' },
		{ fieldName: 'invoicedTotal', fieldLabel: 'Invoiced' },
		// PD-17135: sent only to users who may see gross profit.
		{ fieldName: 'orderedGp', fieldLabel: 'Ordered GP' },
		{ fieldName: 'orderedGpPercent', fieldLabel: 'Ordered GP %' },
		{ fieldName: 'invoicedGp', fieldLabel: 'Invoiced GP' },
		{ fieldName: 'invoicedGpPercent', fieldLabel: 'Invoiced GP %' },
		{ fieldName: 'lastOrderDate', fieldLabel: 'Last Order' },
		{ fieldName: '_created_date', fieldLabel: 'Created Date' },
	],
};

const CLIENT_OBJECT = {
	baseUrl: '/clients/:id',
	objectLabel: 'Customer',
	objectTable: 'client',
	selectedFields: [{ fieldName: 'fkClientId', fieldLabel: 'ID' }],
};

const JOB_TABLE_SORTS: ListBuilderConfiguration['tableSorts'] = [
	{
		id: 'start-desc',
		name: 'Newest Start First',
		fields: [{ direction: 'DESC', fieldName: 'startDate', objectTable: JOB_TABLE }],
		description: 'Jobs that started most recently first',
	},
	{
		id: 'name',
		name: 'Name',
		fields: [{ direction: 'ASC', fieldName: 'name', objectTable: JOB_TABLE }],
		description: 'Sort jobs by name',
	},
	{
		id: 'estimate-desc',
		name: 'Largest Estimate',
		fields: [{ direction: 'DESC', fieldName: 'estimatedValue', objectTable: JOB_TABLE }],
		description: 'Largest estimated value first',
	},
	{
		id: 'ordered-desc',
		name: 'Most Ordered',
		fields: [{ direction: 'DESC', fieldName: 'orderedTotal', objectTable: JOB_TABLE }],
		description: 'Most ordered to date first',
	},
	{
		id: 'last-order-desc',
		name: 'Most Recent Order',
		fields: [{ direction: 'DESC', fieldName: 'lastOrderDate', objectTable: JOB_TABLE }],
		description: 'Jobs with the most recent order first',
	},
	{
		id: 'closed-desc',
		name: 'Recently Closed',
		fields: [{ direction: 'DESC', fieldName: 'closedDate', objectTable: JOB_TABLE }],
		description: 'Most recently closed jobs first',
	},
	{
		id: 'created-desc',
		name: 'Newest First',
		fields: [{ direction: 'DESC', fieldName: '_created_date', objectTable: JOB_TABLE }],
		description: 'Most recently added jobs first',
	},
];

export const JOB_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		list('default-jobs-open', 'Open', 0, 'Planned, active and on-hold jobs', {
			jobStatus: OPEN_STATUS_RULE,
		}),
		list('default-jobs-mine', 'My Jobs', 1, 'Open jobs where you are the salesperson', {
			jobStatus: OPEN_STATUS_RULE,
			jobSalespeople: {
				defaultValue: [{ id: 'ME', label: 'Me' }],
				locked: true,
			},
		}),
		list('default-jobs-active', 'Active', 2, 'Jobs under way', {
			jobStatus: statusRule('active', 'Active'),
		}),
		list(
			'default-jobs-closed',
			'Closed',
			3,
			'Closed jobs',
			{ jobStatus: statusRule('closed', 'Closed') },
			createDefaultJobColumns(),
			'closed-desc',
		),
		list('default-jobs-all', 'All', 4, 'Every job', {}),
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50],
	},
	filters: {
		activeFilters: [
			'jobKeywords',
			'jobStatus',
			'jobClients',
			'jobSalespeople',
			'jobStartDate',
			'jobEndDate',
			'jobLastOrderDate',
			'jobEstimatedValue',
		],
		defaultFilters: {},
	},
	objects: [JOB_OBJECT, CLIENT_OBJECT],
	tableSorts: JOB_TABLE_SORTS,
};

/**
 * A customer's Jobs tab: the customer is passed by the tab itself
 * (`jobClients`), so the tabs here only split open from closed. PD-17448:
 * that filter matches every job the customer is on, and each row carries the
 * customer's role and status there.
 */
export const CLIENT_JOBS_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		list(
			'default-client-jobs-open',
			'Open',
			0,
			'Planned, active and on-hold jobs',
			{ jobStatus: OPEN_STATUS_RULE },
			createClientJobColumns(),
		),
		list(
			'default-client-jobs-closed',
			'Closed',
			1,
			'Closed jobs',
			{ jobStatus: statusRule('closed', 'Closed') },
			createClientJobColumns(),
			'closed-desc',
		),
		list('default-client-jobs-all', 'All', 2, 'Every job', {}, createClientJobColumns()),
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50],
	},
	filters: {
		activeFilters: ['jobKeywords', 'jobStatus', 'jobSalespeople', 'jobStartDate', 'jobEndDate'],
		defaultFilters: {},
	},
	objects: [JOB_OBJECT],
	tableSorts: JOB_TABLE_SORTS,
};
